import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import api from './services/api';
import './App.css';
import AdminPage from './AdminPage';
import SettingsPage from './SettingsPage';
import Header from './components/Header';
import SmsOptIn from './components/SmsOptIn';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import PasswordInput from './components/PasswordInput';
import LandingPage from './components/LandingPage';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import ChangePromptModal from './components/ChangePromptModal';
import FilterBox from './components/FilterBox';
import ReflectionCard from './components/ReflectionCard';
import AddPromptModal from './components/AddPromptModal';
import AuthenticationModal from './components/AuthenticationModal';
import TutorialOverlay from './components/Tutorial/TutorialOverlay';
import WelcomeDialog from './components/WelcomeDialog';
import PrivacyChoicesDialog from './components/PrivacyChoicesDialog';
import PhoneVerificationNudge from './components/PhoneVerificationNudge';
import ExportModal from './components/ExportModal';
import Studio from './components/Studio/Studio';
import Home from './components/Home';
import ProcessingDialog from './components/ProcessingDialog';
import StorageLimitDialog from './components/StorageLimitDialog';
import FamilyArchiveView from './components/Family/FamilyArchiveView';
import { useTutorial } from './contexts/TutorialContext';
import { studioTutorial } from './tutorials/studioTutorial';
import { API_BASE_URL, IS_DEVELOPMENT } from './config';  // Environment-aware API configuration
import { PAGES, VIEWS, ANSWER_FILTER, DATE_FILTER, CATEGORY_FILTER, STORAGE_KEYS } from './constants';
import { buildViewStatusMap, buildViewStatusMapFromBatch, isReflectionNew as checkReflectionNew, matchesDateFilter as checkDateFilter, matchesSearch, promptMatchesSearch, parseSearchText, hasUserFilter } from './utils/viewStatusHelpers';
import useWebSocket from './hooks/useWebSocket';
import useAuth from './hooks/useAuth';
import { localToUTC, formatDateOnly } from './utils/dateUtils';

function App() {
  const { startTutorial, isActive: tutorialIsActive, currentTutorial } = useTutorial();

  // Auth hook — manages user, token, login/register, verification, password, preferences
  const {
    user, setUser, token, isAdmin, logout: authLogout, getAxiosConfig,
    userPreferences, setUserPreferences, effectiveAISettings,
    showLoginModal, setShowLoginModal, loginMode, setLoginMode,
    credentials, setCredentials, smsConsent, setSmsConsent,
    handleAuth,
    showVerificationModal, setShowVerificationModal,
    verificationData, verificationCode, setVerificationCode,
    resendCooldown, resending,
    handleVerifyCode, handleResendVerification,
    showPhoneVerificationNudge, dismissPhoneVerificationNudge,
    showPasswordModal, setShowPasswordModal,
    passwordData, setPasswordData,
    handlePasswordChange,
    showForgotPassword, setShowForgotPassword,
    forgotEmail, setForgotEmail,
    resetMessage, setResetMessage,
    handleForgotPassword,
    showResetPassword,
    newPassword, setNewPassword,
    confirmNewPassword, setConfirmNewPassword,
    resetError, resetSuccess,
    handleResetPassword,
    showWelcomeDialog, setShowWelcomeDialog, isWelcomeBack,
  } = useAuth();

  // Wrap auth logout with navigation
  const logout = () => {
    authLogout();
    navigateToPage(PAGES.MAIN);
  };

  // Set document title based on environment
  useEffect(() => {
    document.title = IS_DEVELOPMENT ? 'Brings to Mind (Dev)' : 'Brings to Mind';
  }, []);

  // Initialize state from URL parameters
  const getInitialStateFromURL = () => {
    const params = new URLSearchParams(window.location.search);
    const page = params.get('page') || PAGES.MAIN;
    const view = params.get('view') || VIEWS.HOME;
    return {
      page,
      view
    };
  };

  const initialState = getInitialStateFromURL();
  const [currentPage, setCurrentPage] = useState(initialState.page); // PAGES.MAIN, PAGES.ADMIN, or PAGES.SETTINGS
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [view, setView] = useState(initialState.view);
  const [studioPrompt, setStudioPrompt] = useState(null);
  const [studioReflection, setStudioReflection] = useState(null);
  const [studioReadOnly, setStudioReadOnly] = useState(false);
  const [lastTranscriptionUpdate, setLastTranscriptionUpdate] = useState(null);
  const [lastEnhancementUpdate, setLastEnhancementUpdate] = useState(null);
  const [lastTitleUpdate, setLastTitleUpdate] = useState(null);
  const [studioParentReflectionId, setStudioParentReflectionId] = useState(null);
  const [studioFollowupQuestion, setStudioFollowupQuestion] = useState(null);
  const [studioKey, setStudioKey] = useState(0);
  const [storageStatus, setStorageStatus] = useState(null);
  const [showStorageLimitDialog, setShowStorageLimitDialog] = useState(false);
  // First-time-only privacy choices, shown before WelcomeDialog. Move this flag/render
  // block to relocate the dialog elsewhere in the flow.
  const [privacyChoicesShown, setPrivacyChoicesShown] = useState(false);
  const [storageLimitType, setStorageLimitType] = useState('storage');
  const [storageWarningMessage, setStorageWarningMessage] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [reflections, setReflections] = useState([]);
  const [reflectionViewStatuses, setReflectionViewStatuses] = useState({}); // View status for "NEW" badges
  const [selectedPromptId, setSelectedPromptId] = useState(null);
  const [expandedPromptId, setExpandedPromptId] = useState(null);
  const [promptReflections, setPromptReflections] = useState({});
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showDeleteReflectionModal, setShowDeleteReflectionModal] = useState(false);
  const [reflectionToDelete, setReflectionToDelete] = useState(null);
  const [showChangePromptModal, setShowChangePromptModal] = useState(false);
  const [changePromptReflection, setChangePromptReflection] = useState(null);
  const [allActivePrompts, setAllActivePrompts] = useState([]);
  const [processingDialog, setProcessingDialog] = useState(null);
  const [treeAutoExpandCardId, setTreeAutoExpandCardId] = useState(null);

  // Ref to track where the user navigated FROM when entering studio
  // Used by both onClose and popstate to restore scroll position and tree expansion
  const studioReturnContextRef = useRef(null);

  // Studio unsaved-text guard: registered by Studio, called before navigating away
  const studioBeforeLeaveRef = useRef(null);
  const studioIsDirtyRef = useRef(false);

  // Filter state
  const [showFilterBox, setShowFilterBox] = useState(false);
  const [hideShared, setHideShared] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [answerFilter, setAnswerFilter] = useState('show all'); // 'show all', 'unanswered only', 'answered only', 'new only'
  const [dateFilterType, setDateFilterType] = useState('none'); // 'none', 'last-week', 'last-month', 'since', 'between'
  const [dateFilterSince, setDateFilterSince] = useState('');
  const [dateFilterFrom, setDateFilterFrom] = useState('');
  const [dateFilterTo, setDateFilterTo] = useState('');

  // ⚠️ IMPORTANT: Recording State Management - READ BEFORE MODIFYING ⚠️
  // isRecording tracks if ANY video/audio recording (or countdown) is active in the app
  // When isRecording=true, MOST interactive elements should be disabled except:
  //   - The active recording controls (stop button, media controls in RecordingSection)
  //   - The section currently being recorded (Video or Audio)
  // This ensures users stay focused on recording without accidental navigation/edits
  //
  // Elements disabled during recording (see implementations below):
  //   ✗ Header, categories, view toggle (blurred with pointer-events:none)
  //   ✗ Other prompt cards (blurred with pointer-events:none)
  //   ✗ Show/Edit buttons on other reflections (disabled attribute + opacity)
  //   ✗ Title input on other reflections (disabled attribute)
  //   ✗ Section headers not currently recording (disabled class + early return)
  //   ✗ Text content textarea (disabled attribute)
  //
  // If adding new interactive elements, ensure they are disabled during isRecording=true
  const [isRecording, setIsRecording] = useState(false); // Track if any recording is active
  const [recordingPromptId, setRecordingPromptId] = useState(null); // Track which prompt is being recorded
  const [showAddPromptModal, setShowAddPromptModal] = useState(false);

  // Fetch categories when authenticated
  const categoriesFetchDone = useRef(false);
  useEffect(() => {
    if (token && !categoriesFetchDone.current) {
      categoriesFetchDone.current = true;
      fetchCategories();
      // Pre-fetch subscription status so Family nav button renders immediately
      api.get('/subscriptions/current')
        .then(r => setStorageStatus(prev => ({ ...prev, ...r.data })))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (selectedCategory && token) {
      fetchPrompts();
      if (view === 'reflections') {
        fetchReflections();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, view, token, dateFilterType, dateFilterSince, dateFilterFrom, dateFilterTo]);

  // Recording-related useEffects removed - now handled by ExpandedReflectionView component
  // Listen for browser back/forward button and sync state with URL
  useEffect(() => {
    const handlePopstate = () => {
      const params = new URLSearchParams(window.location.search);
      const page = params.get('page') || 'main';
      const newView = params.get('view') || 'home';

      // Only update state if values actually changed to prevent loops
      setCurrentPage(prevPage => prevPage === page ? prevPage : page);
      setView(prevView => {
        // Detect studio → non-studio transition (back button from studio)
        if (prevView === 'studio' && newView !== 'studio') {
          // Check if Studio has unsaved text changes
          if (studioIsDirtyRef.current && studioBeforeLeaveRef.current) {
            // Push back the studio URL to cancel the browser navigation
            window.history.pushState({}, '', window.location.pathname + '?' + new URLSearchParams({ page: 'main', view: 'studio' }).toString());
            // Show the dialog via Studio's guard
            studioBeforeLeaveRef.current().then(result => {
              if (result !== 'cancel') {
                // User chose to leave — clear studio and navigate
                const returnContext = studioReturnContextRef.current;
                studioReturnContextRef.current = null;
                setStudioPrompt(null);
                setStudioReflection(null);
                setStudioReadOnly(false);
                setStudioParentReflectionId(null);
                setStudioFollowupQuestion(null);
                changeView(newView);
                if (returnContext && newView === 'prompts') {
                  restorePromptsView(returnContext);
                }
              }
            });
            return prevView; // Stay in studio for now
          }

          const returnContext = studioReturnContextRef.current;
          studioReturnContextRef.current = null;

          // Clear studio state
          setStudioPrompt(null);
          setStudioReflection(null);
          setStudioReadOnly(false);
          setStudioParentReflectionId(null);
          setStudioFollowupQuestion(null);

          // Restore prompts view with scroll and tree expansion
          if (returnContext && newView === 'prompts') {
            restorePromptsView(returnContext);
          }
        }
        return prevView === newView ? prevView : newView;
      });
    };

    window.addEventListener('popstate', handlePopstate);
    return () => window.removeEventListener('popstate', handlePopstate);
  }, []);

  // Restore studio state from URL params on page load/popstate
  useEffect(() => {
    if (view !== 'studio' || studioPrompt) return;

    const params = new URLSearchParams(window.location.search);
    const promptId = params.get('studioPromptId');

    if (!promptId) {
      // No studio context in URL - redirect to prompts
      setView('home');
      updateURLParams({ view: 'home' }, true);
      return;
    }

    const restoreStudio = async () => {
      try {
        // Fetch all data before setting any state so Studio mounts with complete context
        const reflectionId = params.get('studioReflectionId');
        const [promptRes, refRes] = await Promise.all([
          api.get(`/prompts/${promptId}`),
          reflectionId ? api.get(`/reflections/${reflectionId}`) : Promise.resolve(null)
        ]);

        // Set all state in one batch so Studio renders with the existing reflection
        setStudioPrompt(promptRes.data);
        setStudioReflection(refRes?.data || null);
        setStudioReadOnly(params.get('studioReadOnly') === '1');
      } catch (err) {
        console.error('Failed to restore studio state:', err);
        setView('home');
        updateURLParams({ view: 'home', studioPromptId: null, studioReflectionId: null, studioReadOnly: null }, true);
      }
    };

    restoreStudio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // Load reflections for prompts when search text changes (using batch endpoint)
  useEffect(() => {
    const loadReflectionsForSearch = async () => {
      // Only load if there's a search term
      if (!searchText.trim()) return;

      // Get all prompts that don't have reflections loaded yet
      const promptsToLoad = prompts.filter(p => !promptReflections[p.id]);

      console.log('Search text:', searchText);
      console.log('Total prompts:', prompts.length);
      console.log('Prompts to load reflections for:', promptsToLoad.length);

      if (promptsToLoad.length === 0) {
        console.log('All prompts already have reflections loaded');
        return;
      }

      try {
        // Use batch endpoint to load all reflections in one request
        const promptIds = promptsToLoad.map(p => p.id);

        console.log('Batch loading reflections for', promptIds.length, 'prompts');

        const response = await api.post('/reflections/batch', { promptIds });

        // Response is already grouped by prompt_id
        const reflectionsByPrompt = response.data;

        console.log('Loaded reflections for', Object.keys(reflectionsByPrompt).length, 'prompts');

        const viewStatusMap = buildViewStatusMapFromBatch(reflectionsByPrompt);

        // Update reflectionViewStatuses with extracted view statuses
        setReflectionViewStatuses(prev => ({
          ...prev,
          ...viewStatusMap
        }));

        // Update promptReflections with all the data
        setPromptReflections(prev => {
          const newReflections = { ...prev };

          // Add loaded reflections
          Object.entries(reflectionsByPrompt).forEach(([promptId, reflections]) => {
            newReflections[parseInt(promptId)] = reflections;
          });

          // Add empty arrays for prompts with no reflections
          promptIds.forEach(promptId => {
            if (!newReflections[promptId]) {
              newReflections[promptId] = [];
            }
          });

          return newReflections;
        });

        console.log('Finished loading all reflections for search');
      } catch (error) {
        console.error('Error loading reflections for search:', error);
      }
    };

    loadReflectionsForSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, prompts]);

  // Helper function to update URL parameters without page reload
  // usePushState: true for major navigation (creates history entry), false for refinements
  const updateURLParams = (updates, usePushState = false) => {
    const params = new URLSearchParams(window.location.search);

    // Apply updates
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === undefined) {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    });

    // Update URL without page reload
    const newURL = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;

    // Use pushState for major navigation, replaceState for refinements
    if (usePushState) {
      window.history.pushState({}, '', newURL);
    } else {
      window.history.replaceState({}, '', newURL);
    }
  };

  // Wrapper functions to update state AND URL
  // Major navigation: page changes (creates history entry)
  const navigateToPage = (page) => {
    // Prevent infinite loops - don't navigate if already on that page
    if (currentPage === page) {
      return;
    }

    setCurrentPage(page);
    // Clear tab parameter when changing pages (each page has different tabs)
    updateURLParams({ page, tab: null }, true); // pushState for major navigation
  };

  // Major navigation: view changes (creates history entry)
  const changeView = (newView) => {
    // Prevent infinite loops - don't change view if already on that view
    if (view === newView) {
      return;
    }

    setView(newView);

    // When leaving studio, clean up studio URL params
    if (newView !== 'studio') {
      updateURLParams({ view: newView, studioPromptId: null, studioReflectionId: null, studioReadOnly: null }, true);
    } else {
      updateURLParams({ view: newView }, true);
    }
  };

  // Guarded view change — checks Studio's unsaved-text guard before leaving studio
  const guardedChangeView = async (newView) => {
    if (view === 'studio' && newView !== 'studio' && studioBeforeLeaveRef.current) {
      const result = await studioBeforeLeaveRef.current();
      if (result === 'cancel') return;
    }
    changeView(newView);
  };

  // Open studio with prompt context persisted to URL for refresh support
  const openStudio = async (prompt, reflection = null, readOnly = false, opts = {}) => {
    // Check storage quota before opening for new/editable content
    if (!readOnly) {
      try {
        const response = await api.get('/users/storage-status');
        const status = response.data;
        setStorageStatus(status);

        if (status.isOverLimit) {
          setStorageLimitType('storage');
          setShowStorageLimitDialog(true);
          return; // Don't open Studio
        }

        // Check reflection count limit (Free plan)
        if (status.reflectionLimit && status.reflectionCount >= status.reflectionLimit && status.isUnrestricted === false) {
          setStorageLimitType('reflections');
          setShowStorageLimitDialog(true);
          return;
        }

        if (status.isWarning) {
          setStorageWarningMessage(status.warningMessage);
        } else {
          setStorageWarningMessage(null);
        }
      } catch (err) {
        // Fail-open: if storage check fails, allow Studio to open
        console.error('Error checking storage status:', err);
        setStorageWarningMessage(null);
      }
    } else {
      setStorageWarningMessage(null);
    }

    const { sourceCardId = null, fromTree = false } = opts;
    // Save return context for scroll/tree restoration when coming back
    studioReturnContextRef.current = {
      promptId: prompt.id,
      categorySlug: prompt.category_slug,
      sourceCardId: sourceCardId || reflection?.id || null,
      fromTree,
      selectedCategory
    };
    setStudioPrompt(prompt);
    setStudioReflection(reflection);
    setStudioReadOnly(readOnly);
    setView('studio');
    updateURLParams({
      view: 'studio',
      studioPromptId: prompt.id,
      studioReflectionId: reflection?.id || null,
      studioReadOnly: readOnly ? '1' : null
    }, true);
  };

  const fetchCategories = async () => {
    try {
      const res = await api.get('/prompts/categories');
      console.log('Categories fetched:', res.data);
      setCategories(res.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchPrompts = async () => {
    try {
      console.log('Fetching prompts for category:', selectedCategory);

      // Build query parameters including date filters
      const params = {};
      if (dateFilterType && dateFilterType !== 'none') {
        params.dateFilterType = dateFilterType;
        if (dateFilterType === 'since' && dateFilterSince) {
          params.dateFilterSince = localToUTC(dateFilterSince, user?.timezone);
        } else if (dateFilterType === 'between' && dateFilterFrom && dateFilterTo) {
          params.dateFilterFrom = localToUTC(dateFilterFrom, user?.timezone);
          params.dateFilterTo = localToUTC(dateFilterTo, user?.timezone);
        }
      }

      const res = await api.get(`/prompts/category/${selectedCategory}`, { params });
      console.log('Prompts fetched:', res.data);
      setPrompts(res.data);
    } catch (error) {
      console.error('Error fetching prompts:', error);
    }
  };

  const fetchReflections = async () => {
    try {
      const params = selectedCategory !== 'all' ? { category: selectedCategory } : {};
      const res = await api.get('/reflections', { params });
      setReflections(res.data);
      setReflectionViewStatuses(buildViewStatusMap(res.data));
    } catch (error) {
      console.error('Error fetching reflections:', error);
    }
  };

  // Determine if reflection should show "NEW" badge
  const isReflectionNew = useCallback((reflection) => {
    return checkReflectionNew(reflection, reflectionViewStatuses, user?.id);
  }, [reflectionViewStatuses, user?.id]);

  // Check if a reflection OR any of its descendants in the tree are new
  // Used for ReflectionCard badges — card shows NEW if anything in the thread is unseen
  const isReflectionTreeNew = useCallback((reflection, allReflections) => {
    if (isReflectionNew(reflection)) return true;
    // Collect all descendant IDs via parent_reflection_id chain
    const descendants = [];
    const findChildren = (parentId) => {
      for (const r of allReflections) {
        if (r.parent_reflection_id === parentId) {
          descendants.push(r);
          findChildren(r.id);
        }
      }
    };
    findChildren(reflection.id);
    return descendants.some(r => isReflectionNew(r));
  }, [isReflectionNew]);

  // Compute tree-inclusive match sets for search filtering
  // Returns { matchIds: Set of direct matches, visibleIds: Set of matchIds + all tree relatives }
  const getTreeMatchSets = useCallback((allReflections, searchText) => {
    if (!searchText?.trim()) return { matchIds: new Set(), visibleIds: new Set() };

    const matchIds = new Set();
    allReflections.forEach(r => {
      if (matchesSearch(r, searchText)) matchIds.add(r.id);
    });

    if (matchIds.size === 0) return { matchIds, visibleIds: new Set() };

    // Build lookup for tree traversal
    const byId = {};
    allReflections.forEach(r => { byId[r.id] = r; });

    // Find root of a reflection's tree
    const findRoot = (id) => {
      const r = byId[id];
      if (r?.parent_reflection_id && byId[r.parent_reflection_id]) {
        return findRoot(r.parent_reflection_id);
      }
      return id;
    };

    // Add all descendants of a node
    const addDescendants = (rootId, visibleIds) => {
      visibleIds.add(rootId);
      allReflections.filter(r => r.parent_reflection_id === rootId).forEach(child => {
        addDescendants(child.id, visibleIds);
      });
    };

    // Expand to include full trees for every match
    const visibleIds = new Set(matchIds);
    matchIds.forEach(id => {
      const rootId = findRoot(id);
      addDescendants(rootId, visibleIds);
    });

    return { matchIds, visibleIds };
  }, []);

  // Check if a prompt has any new (unviewed) reflections
  const promptHasNewReflections = (prompt) => {
    // Badges are only for shared reflections; hideShared means no badges
    if (hideShared) return false;

    const allReflections = promptReflections[prompt.id];

    // If reflections haven't been loaded yet, use backend-provided count
    if (allReflections === undefined) {
      return (parseInt(prompt.unviewed_count) || 0) > 0;
    }

    // Apply same filtering as displayed reflections
    const filteredReflections = allReflections.filter(r => {
      // Only check shared reflections (own reflections never show badges)
      if (r.user_id === user?.id) return false;

      // Respect search filter - supports user:xxx pattern
      if (!matchesSearch(r, searchText)) {
        return false;
      }

      return true;
    });

    // Check if any filtered reflection is new
    return filteredReflections.some(r => isReflectionNew(r));
  };

  // Handle prompt badge dismissal - marks all reflections in prompt as viewed
  const handleDismissPromptBadge = async (prompt, event) => {
    event.stopPropagation();

    try {
      const allReflections = promptReflections[prompt.id] || [];

      // Get IDs of reflections that would show badges (same filtering as promptHasNewReflections)
      const reflectionIds = allReflections
        .filter(r => {
          // Only shared reflections
          if (r.user_id === user?.id) return false;

          // Respect hideShared filter
          if (hideShared && r.user_id !== user?.id) return false;

          // Respect search filter - supports user:xxx pattern
          if (!matchesSearch(r, searchText)) {
            return false;
          }

          return true;
        })
        .map(r => r.id);

      if (reflectionIds.length === 0) return;

      // Mark all as viewed
      await api.post('/reflection-views/bulk-mark-viewed', { reflectionIds });

      // Refresh to get updated view statuses
      await refreshExpandedPromptReflections();

    } catch (error) {
      console.error('Error dismissing prompt badge:', error);
    }
  };

  // Handle badge dismissal
  const handleDismissBadge = async (reflectionId, event) => {
    event.stopPropagation();
    try {
      await api.post('/reflection-views/dismiss-badge', { reflectionId });
      setReflectionViewStatuses(prev => ({
        ...prev,
        [reflectionId]: { ...prev[reflectionId], badge_dismissed: true }
      }));
    } catch (error) {
      console.error('Error dismissing badge:', error);
    }
  };

  // Handle view status change (called when media is played or text is expanded)
  const handleViewStatusChange = () => {
    // Refresh the reflection data to get updated view status
    fetchReflections();
  };

  // Memoized date filter config object — convert datetime-local inputs to UTC
  const dateFilter = useMemo(() => ({
    type: dateFilterType,
    since: dateFilterSince ? localToUTC(dateFilterSince, user?.timezone) : dateFilterSince,
    from: dateFilterFrom ? localToUTC(dateFilterFrom, user?.timezone) : dateFilterFrom,
    to: dateFilterTo ? localToUTC(dateFilterTo, user?.timezone) : dateFilterTo
  }), [dateFilterType, dateFilterSince, dateFilterFrom, dateFilterTo, user?.timezone]);

  const matchesDateFilter = useCallback((reflection) => {
    return checkDateFilter(reflection, dateFilter);
  }, [dateFilter]);

  // Memoized active filter count for the filter button label
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (hideShared) count++;
    if (searchText.trim()) count++;
    if (answerFilter !== 'show all') count++;
    if (dateFilterType !== 'none') count++;
    return count > 0 ? `(${count})` : '';
  }, [hideShared, searchText, answerFilter, dateFilterType]);

  // Get all currently filtered reflection IDs
  // This includes reflections from ALL prompts that match the current filters
  const getFilteredReflectionIds = async () => {
    // First, ensure all prompts have their reflections loaded
    const promptsToLoad = prompts.filter(p => !promptReflections[p.id]);

    // Create a combined reflections object that includes both existing and newly loaded
    let allPromptReflections = { ...promptReflections };

    if (promptsToLoad.length > 0) {
      try {
        const promptIds = promptsToLoad.map(p => p.id);
        console.log('Loading reflections for', promptIds.length, 'prompts before filtering');

        const response = await api.post('/reflections/batch', { promptIds });

        // Response is already grouped by prompt_id
        const reflectionsByPrompt = response.data;

        // Update state with loaded reflections
        setReflectionViewStatuses(prev => ({
          ...prev,
          ...buildViewStatusMapFromBatch(reflectionsByPrompt)
        }));

        setPromptReflections(prev => ({
          ...prev,
          ...reflectionsByPrompt
        }));

        // Merge newly loaded reflections with existing ones for immediate use
        allPromptReflections = {
          ...allPromptReflections,
          ...reflectionsByPrompt
        };

        console.log('Loaded reflections for', Object.keys(reflectionsByPrompt).length, 'prompts');
      } catch (error) {
        console.error('Error loading reflections:', error);
        throw error;
      }
    }

    const allFilteredReflectionIds = [];

    // Iterate through all prompts that match the current filters
    prompts.forEach(prompt => {
      // Apply same prompt filtering logic as in the UI

      // Filter 1: Hide Shared (prompt level)
      if (hideShared) {
        const sharedCount = parseInt(prompt.shared_count) || 0;
        if (sharedCount > 0) {
          const reflections = allPromptReflections[prompt.id] || [];
          const hasSharedReflections = reflections.some(r => r.user_id !== user?.id);
          if (hasSharedReflections && reflections.every(r => r.user_id !== user?.id)) {
            // All reflections are shared, skip this prompt
            return;
          }
        }
      }

      // Filter 2: Search Text (prompt level)
      // Supports user:xxx pattern for filtering by username
      if (searchText.trim()) {
        // Check if prompt text matches (ignores user: filter)
        const promptMatches = promptMatchesSearch(prompt.prompt_text, searchText);

        // Check if any reflection matches (supports user:xxx pattern)
        const reflections = allPromptReflections[prompt.id] || [];
        const matchesReflection = reflections.some(r => matchesSearch(r, searchText));

        if (!promptMatches && !matchesReflection) {
          // Prompt doesn't match search, skip it
          return;
        }
      }

      // Filter 3: Answer Filter (prompt level)
      if (answerFilter === 'unanswered only') {
        const myCount = parseInt(prompt.my_count) || 0;
        if (myCount > 0) return;
      } else if (answerFilter === 'answered only') {
        const myCount = parseInt(prompt.my_count) || 0;
        const sharedCount = parseInt(prompt.shared_count) || 0;
        const visibleSharedCount = hideShared ? 0 : sharedCount;
        if (myCount === 0 && visibleSharedCount === 0) return;
      }

      // Prompt passed filters, now filter its reflections
      const allReflections = allPromptReflections[prompt.id] || [];
      const filteredReflections = allReflections.filter(r => {
        // ONLY include shared reflections (exclude own reflections since they never show badges)
        if (r.user_id === user?.id) {
          return false;
        }

        // Category filter
        if (selectedCategory !== 'all' && r.category_slug !== selectedCategory) {
          return false;
        }

        // Hide shared filter (reflection level)
        if (hideShared && r.user_id !== user?.id) {
          return false;
        }

        // Search text filter (reflection level) - supports user:xxx pattern
        if (!matchesSearch(r, searchText)) {
          return false;
        }

        // Date filter
        if (!matchesDateFilter(r)) {
          return false;
        }

        return true;
      });

      // Add all filtered reflection IDs from this prompt
      filteredReflections.forEach(r => {
        allFilteredReflectionIds.push(r.id);
      });
    });

    return allFilteredReflectionIds;
  };

  // Mark all filtered reflections as viewed
  const handleMarkAllViewed = async () => {
    try {
      const reflectionIds = await getFilteredReflectionIds();

      if (reflectionIds.length === 0) {
        alert('No reflections match the current filter');
        return;
      }

      const confirmMsg = `Mark ${reflectionIds.length} reflection(s) as viewed?`;
      if (!window.confirm(confirmMsg)) {
        return;
      }

      await api.post('/reflection-views/bulk-mark-viewed', { reflectionIds });

      // Refresh to get updated view statuses
      if (expandedPromptId) {
        // If viewing prompts, refresh the expanded prompt's reflections
        await refreshExpandedPromptReflections();
      } else {
        // If in reflections view, refresh all reflections
        fetchReflections();
      }

      alert(`Successfully marked ${reflectionIds.length} reflection(s) as viewed`);
    } catch (error) {
      console.error('Error marking all as viewed:', error);
      alert('Failed to mark reflections as viewed');
    }
  };

  // Mark all filtered reflections as unviewed
  const handleMarkAllUnviewed = async () => {
    try {
      const reflectionIds = await getFilteredReflectionIds();

      if (reflectionIds.length === 0) {
        alert('No reflections match the current filter');
        return;
      }

      const confirmMsg = `Mark ${reflectionIds.length} reflection(s) as unviewed (NEW)?`;
      if (!window.confirm(confirmMsg)) {
        return;
      }

      await api.post('/reflection-views/bulk-mark-unviewed', { reflectionIds });

      // Refresh to get updated view statuses
      if (expandedPromptId) {
        // If viewing prompts, refresh the expanded prompt's reflections
        await refreshExpandedPromptReflections();
      } else {
        // If in reflections view, refresh all reflections
        fetchReflections();
      }

      alert(`Successfully marked ${reflectionIds.length} reflection(s) as unviewed`);
    } catch (error) {
      console.error('Error marking all as unviewed:', error);
      alert('Failed to mark reflections as unviewed');
    }
  };

  const refreshExpandedPromptReflections = async () => {
    if (expandedPromptId) {
      try {
        const res = await api.get('/reflections', {
          params: { promptId: expandedPromptId }
        });

        // Extract view statuses from reflections
        const viewStatusMap = {};
        res.data.forEach(reflection => {
          viewStatusMap[reflection.id] = {
            reflection_id: reflection.id,
            is_viewed: reflection.is_viewed || false,
            badge_dismissed: reflection.badge_dismissed || false,
            last_viewed_at: reflection.last_viewed_at,
            video_played: reflection.video_played,
            audio_played: reflection.audio_played,
            text_expanded: reflection.text_expanded
          };
        });

        // Update view statuses
        setReflectionViewStatuses(prev => ({
          ...prev,
          ...viewStatusMap
        }));

        setPromptReflections({ ...promptReflections, [expandedPromptId]: res.data });
      } catch (error) {
        console.error('Error refreshing reflections:', error);
      }
    }
  };

  // Handle recording state changes for blur effect
  const handleRecordingStateChange = (recording, promptId) => {
    setIsRecording(recording);
    setRecordingPromptId(recording ? promptId : null);
  };

  // Handle transcription status update (from WebSocket or manual trigger)
  const handleTranscriptionUpdate = useCallback((updatedReflection) => {
    // Only include fields that are actually present in the update
    // to avoid overwriting existing transcription data with undefined
    const transcriptionFields = {};
    if (updatedReflection.video_transcription_status !== undefined) {
      transcriptionFields.video_transcription_status = updatedReflection.video_transcription_status;
    }
    if (updatedReflection.video_transcription_text !== undefined) {
      transcriptionFields.video_transcription_text = updatedReflection.video_transcription_text;
    }
    if (updatedReflection.audio_transcription_status !== undefined) {
      transcriptionFields.audio_transcription_status = updatedReflection.audio_transcription_status;
    }
    if (updatedReflection.audio_transcription_text !== undefined) {
      transcriptionFields.audio_transcription_text = updatedReflection.audio_transcription_text;
    }

    // If no transcription fields were provided, nothing to update
    if (Object.keys(transcriptionFields).length === 0) {
      return;
    }

    // Update promptReflections
    setPromptReflections(prev => {
      const newReflections = { ...prev };
      Object.keys(newReflections).forEach(promptId => {
        newReflections[promptId] = newReflections[promptId].map(r =>
          r.id === updatedReflection.id
            ? { ...r, ...transcriptionFields }
            : r
        );
      });
      return newReflections;
    });

    // Update reflections list (for reflections view)
    setReflections(prev =>
      prev.map(r =>
        r.id === updatedReflection.id
          ? { ...r, ...transcriptionFields }
          : r
      )
    );

    // Forward to Studio (if active)
    setLastTranscriptionUpdate({ id: updatedReflection.id, ...transcriptionFields });

    // Auto-enhance after transcription completes (triggered by handleTranscribeAndEnhance)
    if (pendingEnhanceAfterTranscribeRef.current === updatedReflection.id) {
      const hasText = !!(transcriptionFields.video_transcription_text?.trim() ||
                        transcriptionFields.audio_transcription_text?.trim());
      if (hasText) {
        pendingEnhanceAfterTranscribeRef.current = null;
        handleEnhanceRef.current(updatedReflection.id);
      }
    }
  }, []);

  // Handle title generation started (from WebSocket)
  const handleTitleGenerationStarted = useCallback((data) => {
    const updateFields = {
      title_generation_status: 'generating',
      title_generation_source: data.source
    };

    // Update promptReflections
    setPromptReflections(prev => {
      const newReflections = { ...prev };
      Object.keys(newReflections).forEach(promptId => {
        newReflections[promptId] = newReflections[promptId].map(r =>
          r.id === data.id ? { ...r, ...updateFields } : r
        );
      });
      return newReflections;
    });

    // Update reflections list
    setReflections(prev =>
      prev.map(r => r.id === data.id ? { ...r, ...updateFields } : r)
    );

    // Forward to Studio
    setLastTitleUpdate({ id: data.id, generating: true });
  }, []);

  // Handle enhancement update (from WebSocket)
  const handleEnhancementUpdate = useCallback((updatedReflection) => {
    const updateFields = {};
    if (updatedReflection.ai_followup_status !== undefined) {
      updateFields.ai_followup_status = updatedReflection.ai_followup_status;
    }
    if (updatedReflection.ai_followup_suggestions !== undefined) {
      updateFields.ai_followup_suggestions = updatedReflection.ai_followup_suggestions;
    }

    if (Object.keys(updateFields).length === 0) return;

    // Update promptReflections
    setPromptReflections(prev => {
      const newReflections = { ...prev };
      Object.keys(newReflections).forEach(promptId => {
        newReflections[promptId] = newReflections[promptId].map(r =>
          r.id === updatedReflection.id ? { ...r, ...updateFields } : r
        );
      });
      return newReflections;
    });

    // Update reflections list
    setReflections(prev =>
      prev.map(r => r.id === updatedReflection.id ? { ...r, ...updateFields } : r)
    );

    // Forward to Studio
    setLastEnhancementUpdate({ id: updatedReflection.id, ...updateFields });

    // Close processing dialog when enhancement completes or fails
    if (updatedReflection.ai_followup_status === 'completed' || updatedReflection.ai_followup_status === 'failed') {
      setProcessingDialog(prev =>
        prev?.reflectionId === updatedReflection.id ? null : prev
      );
    }
  }, []);

  // Handle title update (from WebSocket when AI generates title)
  const handleTitleUpdate = useCallback((updatedReflection) => {
    // Determine the final status based on source and whether title was generated
    const updateFields = {
      title_generation_status: updatedReflection.title ? 'completed' : 'failed',
      title_generation_source: updatedReflection.source
    };

    if (updatedReflection.title) {
      updateFields.title = updatedReflection.title;
    }

    // Update promptReflections
    setPromptReflections(prev => {
      const newReflections = { ...prev };
      Object.keys(newReflections).forEach(promptId => {
        newReflections[promptId] = newReflections[promptId].map(r =>
          r.id === updatedReflection.id
            ? { ...r, ...updateFields }
            : r
        );
      });
      return newReflections;
    });

    // Update reflections list (for reflections view)
    setReflections(prev =>
      prev.map(r =>
        r.id === updatedReflection.id
          ? { ...r, ...updateFields }
          : r
      )
    );

    // Forward to Studio
    setLastTitleUpdate({ id: updatedReflection.id, title: updatedReflection.title });
  }, []);

  // WebSocket for real-time transcription, title, and enhancement updates
  useWebSocket({
    onTranscriptionUpdate: handleTranscriptionUpdate,
    onTitleGenerationStarted: handleTitleGenerationStarted,
    onTitleUpdate: handleTitleUpdate,
    onEnhancementUpdate: handleEnhancementUpdate,
    enabled: !!user  // Only connect when user is logged in
  });

  // Safety timeout: auto-close processing dialog if WebSocket event never arrives
  useEffect(() => {
    if (!processingDialog) return;
    const timeout = setTimeout(() => {
      console.warn('ProcessingDialog: Safety timeout reached, closing dialog');
      setProcessingDialog(null);
    }, 60000);
    return () => clearTimeout(timeout);
  }, [processingDialog]);

  // Open a follow-up Studio for answering an enhancement question
  const openFollowupStudio = (parentId, question, prompt, sourceCardId = null) => {
    setStudioParentReflectionId(parentId);
    setStudioFollowupQuestion(question);
    setStudioKey(prev => prev + 1);
    openStudio(prompt, null, false, { sourceCardId: sourceCardId || parentId, fromTree: true });
  };

  // Restore prompts view after returning from studio (used by both onClose and popstate)
  const restorePromptsView = (returnContext) => {
    const { promptId, categorySlug, sourceCardId, fromTree, selectedCategory: savedCategory } = returnContext;
    if (!promptId) return;

    // Switch category if current filter would hide the prompt
    if (categorySlug && savedCategory !== 'all' && savedCategory !== categorySlug) {
      setSelectedCategory(categorySlug);
    }

    // Re-fetch reflections, expand prompt, scroll to source card
    api.get('/reflections', {
      params: { promptId }
    }).then(res => {
      setReflectionViewStatuses(prev => ({
        ...prev,
        ...buildViewStatusMap(res.data)
      }));
      setPromptReflections(prev => ({ ...prev, [promptId]: res.data }));
      setExpandedPromptId(promptId);

      // Signal tree auto-expand if navigated from tree
      if (fromTree && sourceCardId) {
        setTreeAutoExpandCardId(sourceCardId);
      }

      // After DOM renders, scroll to source card at 1/3 viewport height
      setTimeout(() => {
        let el = sourceCardId
          ? document.querySelector(`[data-reflection-id="${sourceCardId}"]`)
          : null;
        if (!el) {
          el = document.querySelector(`[data-prompt-id="${promptId}"]`);
        }
        if (el) {
          const top = el.getBoundingClientRect().top + window.pageYOffset - (window.innerHeight / 3);
          window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        }
        setTreeAutoExpandCardId(null);
      }, 200);
    }).catch(err => {
      console.error('Error restoring prompts view:', err);
    });
  };

  // Handle enhance button click (from ReflectionCard or Studio)
  const handleEnhance = async (reflectionId) => {
    try {
      setProcessingDialog({ reflectionId, text: 'Generating follow-up questions...' });
      await api.post(`/reflections/${reflectionId}/enhance`);
    } catch (error) {
      console.error('Error starting enhancement:', error);
      setProcessingDialog(null);
      alert(error.response?.data?.error || 'Failed to start enhancement');
    }
  };

  // Ref for handleEnhance so it can be called from useCallback([]) handlers
  const handleEnhanceRef = useRef(handleEnhance);
  handleEnhanceRef.current = handleEnhance;

  // Track reflections that need auto-enhance after transcription completes
  const pendingEnhanceAfterTranscribeRef = useRef(null);

  // Tutorial AI overrides: temporarily update user preferences when a tutorial
  // with aiOverrides is active, restore original preferences when it ends
  const savedTutorialPrefsRef = useRef(null);

  useEffect(() => {
    if (!tutorialIsActive || !currentTutorial?.aiOverrides) return;

    const overrides = currentTutorial.aiOverrides;
    const saved = {};

    // Save current values and apply overrides
    Object.keys(overrides).forEach(key => {
      saved[key] = userPreferences[key];
      api.put('/preferences', { preferenceKey: key, preferenceValue: overrides[key] }).catch(err =>
        console.error(`Failed to set tutorial preference ${key}:`, err)
      );
    });

    savedTutorialPrefsRef.current = saved;
    setUserPreferences(prev => ({ ...prev, ...overrides }));

    // Restore on cleanup (tutorial end)
    return () => {
      const toRestore = savedTutorialPrefsRef.current;
      if (!toRestore) return;

      Object.keys(toRestore).forEach(key => {
        api.put('/preferences', { preferenceKey: key, preferenceValue: toRestore[key] }).catch(err =>
          console.error(`Failed to restore preference ${key}:`, err)
        );
      });

      setUserPreferences(prev => ({ ...prev, ...toRestore }));
      savedTutorialPrefsRef.current = null;
    };
  }, [tutorialIsActive, currentTutorial]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tutorial demo category: show hidden Demo category during tutorials that use it,
  // navigate to prompts view with demo selected, restore previous state when tutorial ends
  const savedDemoCategoryRef = useRef(null);

  useEffect(() => {
    if (!tutorialIsActive || !currentTutorial?.demoCategory) return;

    const savedCategory = selectedCategory;
    const savedView = view;
    savedDemoCategoryRef.current = { savedCategory, savedView };

    // Fetch categories including demo, find the demo one
    api.get('/prompts/categories?include_demo=true').then(res => {
      const demoCategory = res.data.find(c => c.slug === 'demo');
      if (demoCategory) {
        setCategories(res.data);
        setSelectedCategory('demo');
        if (view !== 'prompts') changeView('prompts');
      }
    }).catch(err => console.error('Failed to fetch demo categories:', err));

    // Cleanup: remove demo category, restore previous state
    return () => {
      const saved = savedDemoCategoryRef.current;
      fetchCategories(); // Re-fetch without demo
      if (saved) {
        setSelectedCategory(saved.savedCategory);
        if (saved.savedView !== 'prompts') changeView(saved.savedView);
      }
      savedDemoCategoryRef.current = null;
    };
  }, [tutorialIsActive, currentTutorial]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle transcribe-then-enhance: start transcription, auto-enhance when text becomes available
  const handleTranscribeAndEnhance = async (reflectionId) => {
    try {
      setProcessingDialog({ reflectionId, text: 'Transcribing...' });
      pendingEnhanceAfterTranscribeRef.current = reflectionId;
      await api.post(`/reflections/${reflectionId}/transcribe`, {});
    } catch (error) {
      console.error('Error starting transcribe-and-enhance:', error);
      pendingEnhanceAfterTranscribeRef.current = null;
      setProcessingDialog(null);
      alert(error.response?.data?.error || 'Failed to start transcription');
    }
  };

  // Recording functions removed - now handled by ExpandedReflectionView component

  // Modal save/cancel functions removed - now handled by ExpandedReflectionView component


  const handleDeleteReflectionClick = (reflection) => {
    setReflectionToDelete(reflection);
    setShowDeleteReflectionModal(true);
  };

  const confirmDeleteReflection = async () => {
    if (!reflectionToDelete) return;

    const promptId = reflectionToDelete.prompt_id;
    const reflectionId = reflectionToDelete.id;

    try {
      await api.delete(`/reflections/${reflectionId}`);

      // Close modal and clear state
      setShowDeleteReflectionModal(false);
      setReflectionToDelete(null);

      // Update promptReflections to remove the deleted reflection
      // This ensures the View button count updates immediately
      setPromptReflections(prev => {
        const updated = { ...prev };
        if (updated[promptId]) {
          updated[promptId] = updated[promptId].filter(r => r.id !== reflectionId);
        }
        return updated;
      });

      // Refresh data from server
      fetchReflections();
      fetchPrompts(); // Refresh to update counts
    } catch (error) {
      alert('Failed to delete reflection');
      setShowDeleteReflectionModal(false);
      setReflectionToDelete(null);
    }
  };

  const cancelDeleteReflection = () => {
    setShowDeleteReflectionModal(false);
    setReflectionToDelete(null);
  };

  // Change Prompt handlers
  const fetchAllActivePrompts = async () => {
    try {
      const response = await api.get('/prompts/all-active');
      setAllActivePrompts(response.data);
    } catch (error) {
      console.error('Failed to fetch all active prompts:', error);
    }
  };

  const handleChangePromptClick = async (reflection) => {
    // Fetch all active prompts if not already loaded
    if (allActivePrompts.length === 0) {
      await fetchAllActivePrompts();
    }
    setChangePromptReflection(reflection);
    setShowChangePromptModal(true);
  };

  const handleChangePromptAccept = async (newPromptId) => {
    if (!changePromptReflection) return;

    // Find the new prompt to get category info
    const newPrompt = allActivePrompts.find(p => p.id === newPromptId);
    const oldPromptId = changePromptReflection.prompt_id;
    const reflectionId = changePromptReflection.id;

    // Update local state - move reflection from old prompt to new prompt
    if (oldPromptId !== newPromptId) {
      // Remove from old prompt's reflections
      setPromptReflections(prev => {
        const updated = { ...prev };
        if (updated[oldPromptId]) {
          updated[oldPromptId] = updated[oldPromptId].filter(r => r.id !== reflectionId);
        }
        return updated;
      });

      // Update reflections list if in reflections view
      setReflections(prev => prev.map(r =>
        r.id === reflectionId
          ? { ...r, prompt_id: newPromptId, category_id: newPrompt?.category_id }
          : r
      ));

      // Refresh prompt counts
      fetchPrompts();

      // Navigate to show the moved reflection
      const newCategory = categories.find(c => c.id === newPrompt?.category_id);

      // If not viewing "All", switch to the new category
      if (selectedCategory !== 'all' && newCategory) {
        setSelectedCategory(newCategory.slug);
      }

      // Load reflections for the new prompt and expand it
      try {
        const res = await api.get('/reflections', {
          params: { promptId: newPromptId }
        });

        setReflectionViewStatuses(prev => ({
          ...prev,
          ...buildViewStatusMap(res.data)
        }));

        setPromptReflections(prev => ({
          ...prev,
          [newPromptId]: res.data
        }));
        setExpandedPromptId(newPromptId);

        // Scroll to the reflection after DOM updates
        setTimeout(() => {
          const reflectionElement = document.querySelector(
            `[data-reflection-id="${reflectionId}"]`
          );
          if (reflectionElement) {
            reflectionElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }, 150);
      } catch (error) {
        console.error('Error loading new prompt reflections:', error);
      }
    }

    setShowChangePromptModal(false);
    setChangePromptReflection(null);
  };

  const handlePromptCreated = (newPrompt) => {
    // Add the newly created prompt to allActivePrompts
    setAllActivePrompts(prev => [...prev, newPrompt]);
    // Refresh prompts list to get updated counts
    fetchPrompts();
  };

  const cancelChangePrompt = () => {
    setShowChangePromptModal(false);
    setChangePromptReflection(null);
  };

  const togglePromptExpansion = async (prompt) => {
    // If clicking the same prompt, collapse it
    if (expandedPromptId === prompt.id) {
      setExpandedPromptId(null);
      return;
    }

    // Expand this prompt and load its reflections
    try {
      const res = await api.get('/reflections', {
        params: { promptId: prompt.id }
      });

      setReflectionViewStatuses(prev => ({
        ...prev,
        ...buildViewStatusMap(res.data)
      }));

      setPromptReflections({ ...promptReflections, [prompt.id]: res.data });
      setExpandedPromptId(prompt.id);
    } catch (error) {
      console.error('Error fetching prompt reflections:', error);
      alert('Failed to load reflections');
    }
  };



  const handleSaveNewPrompt = async (promptData) => {
    try {
      const response = await api.post('/admin/prompts', {
        promptText: promptData.promptText,
        categoryId: promptData.categoryId,
        isActive: true
      });

      // Get the category slug for the selected category
      const selectedCat = categories.find(c => c.id === parseInt(promptData.categoryId));
      const categorySlug = selectedCat?.slug || 'all';

      // Close modal
      setShowAddPromptModal(false);

      // Switch to the category of the new prompt
      setSelectedCategory(categorySlug);

      // Refresh prompts list
      await fetchPrompts();

      // Scroll to the new prompt after a brief delay to ensure it's rendered
      setTimeout(() => {
        const promptElements = document.querySelectorAll('.prompt-card');
        const newPromptElement = Array.from(promptElements).find(el => {
          const text = el.querySelector('.prompt-text')?.textContent;
          return text === promptData.promptText;
        });

        if (newPromptElement) {
          newPromptElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);

      alert('Prompt created successfully');
    } catch (error) {
      console.error('Error creating prompt:', error);
      alert(error.response?.data?.error || 'Failed to create prompt');
    }
  };

  // Show public policy pages (accessible without authentication)
  if (currentPage === 'sms-opt-in') {
    return <SmsOptIn />;
  }

  if (currentPage === 'privacy') {
    return <PrivacyPolicy />;
  }

  if (currentPage === 'terms') {
    return <TermsOfService />;
  }

  if (!token) {
    return (
      <>
        <LandingPage onLoginClick={() => { setLoginMode('login'); setShowLoginModal(true); }} />

        {showLoginModal && (
          <AuthenticationModal
            loginMode={loginMode}
            credentials={credentials}
            smsConsent={smsConsent}
            onAuth={handleAuth}
            onCredentialsChange={setCredentials}
            onSmsConsentChange={setSmsConsent}
            onLoginModeChange={setLoginMode}
            onClose={() => setShowLoginModal(false)}
            showVerificationModal={showVerificationModal}
            verificationData={verificationData}
            resendCooldown={resendCooldown}
            resending={resending}
            onResendVerification={handleResendVerification}
            onVerificationClose={() => { setShowVerificationModal(false); setShowLoginModal(false); }}
            showForgotPassword={showForgotPassword}
            forgotEmail={forgotEmail}
            resetMessage={resetMessage}
            onForgotPassword={handleForgotPassword}
            onForgotEmailChange={setForgotEmail}
            onForgotPasswordClose={(show) => { setShowForgotPassword(show); if (!show) setResetMessage(''); }}
            showResetPassword={showResetPassword}
            newPassword={newPassword}
            confirmNewPassword={confirmNewPassword}
            resetError={resetError}
            resetSuccess={resetSuccess}
            onResetPassword={handleResetPassword}
            onNewPasswordChange={setNewPassword}
            onConfirmNewPasswordChange={setConfirmNewPassword}
          />
        )}
      </>
    );
  }

  // Show settings page
  if (currentPage === 'settings') {
    const params = new URLSearchParams(window.location.search);
    const initialTab = params.get('tab');

    return (
      <SettingsPage
        user={user}
        token={token}
        onNavigateHome={() => navigateToPage('main')}
        onNavigateAdmin={() => navigateToPage('admin')}
        onNavigateToPrompts={() => { navigateToPage('main'); changeView('prompts'); }}
        onNavigateToHome={() => { navigateToPage('main'); changeView('home'); }}
        onLogout={logout}
        isAdmin={isAdmin()}
        onUserUpdate={setUser}
        initialTab={initialTab}
        onTabChange={(tab) => updateURLParams({ tab })}
        onPreferenceChange={(key, value) => setUserPreferences(prev => ({ ...prev, [key]: value }))}
        stickyHeader={userPreferences.sticky_header === true || userPreferences.sticky_header === 'true'}
      />
    );
  }

  // Show admin page if user is admin and on admin page
  if (currentPage === 'admin' && isAdmin()) {
    const params = new URLSearchParams(window.location.search);
    const initialTab = params.get('tab');

    return (
      <AdminPage
        user={user}
        token={token}
        onNavigateHome={() => navigateToPage('main')}
        onNavigateSettings={() => navigateToPage('settings')}
        onNavigateToPrompts={() => { navigateToPage('main'); changeView('prompts'); }}
        onNavigateToHome={() => { navigateToPage('main'); changeView('home'); }}
        onLogout={logout}
        initialTab={initialTab}
        onTabChange={(tab) => updateURLParams({ tab })}
        stickyHeader={userPreferences.sticky_header === true || userPreferences.sticky_header === 'true'}
      />
    );
  }

  return (
    <div className={`app${view === 'studio' ? ' view-studio' : ''}`} style={view === 'studio' ? { '--studio-background-image': "url('/images/studio_bg_1.png')" } : undefined} data-recording={isRecording ? 'active' : null}>
      <Header
        user={user}
        showProfileMenu={showProfileMenu}
        setShowProfileMenu={setShowProfileMenu}
        onSettingsClick={async () => {
          if (view === 'studio' && studioBeforeLeaveRef.current) {
            const result = await studioBeforeLeaveRef.current();
            if (result === 'cancel') return;
          }
          navigateToPage('settings');
        }}
        onAdminClick={async () => {
          if (view === 'studio' && studioBeforeLeaveRef.current) {
            const result = await studioBeforeLeaveRef.current();
            if (result === 'cancel') return;
          }
          navigateToPage('admin');
        }}
        onLogout={logout}
        isAdmin={isAdmin()}
        onTitleClick={null}
        onMarkAllViewed={handleMarkAllViewed}
        onMarkAllUnviewed={handleMarkAllUnviewed}
        onExportClick={() => setShowExportModal(true)}
        onNavigateToPrompts={() => guardedChangeView('prompts')}
        onNavigateToHome={() => guardedChangeView('home')}
        stickyHeader={userPreferences.sticky_header === true || userPreferences.sticky_header === 'true'}
        className={isRecording ? 'blur-when-recording' : ''}
      />

      <div className={`view-toggle ${isRecording ? 'blur-when-recording' : ''}`}>
        <button
          className={view === 'home' ? 'active' : ''}
          onClick={() => guardedChangeView('home')}
        >
          Home
        </button>
        <button
          className={view === 'prompts' ? 'active' : ''}
          onClick={() => guardedChangeView('prompts')}
        >
          Prompts
        </button>
        <button
          className={view === 'reflections' ? 'active' : ''}
          onClick={() => guardedChangeView('reflections')}
        >
          Reflections
        </button>
        {storageStatus?.allowsFamily && (
          <button
            className={view === VIEWS.FAMILY_ARCHIVE ? 'active' : ''}
            onClick={() => guardedChangeView(VIEWS.FAMILY_ARCHIVE)}
          >
            Family
          </button>
        )}
        {studioPrompt && (
          <button
            className={view === 'studio' ? 'active' : ''}
            onClick={() => changeView('studio')}
          >
            Studio
          </button>
        )}
      </div>

      <nav className={`categories ${isRecording ? 'blur-when-recording' : ''}`} style={(view === 'studio' || view === 'home') ? { display: 'none' } : undefined}>
        {categories.map(cat => (
          <button
            key={cat.id}
            className={selectedCategory === cat.slug ? 'active' : ''}
            onClick={() => setSelectedCategory(cat.slug)}
          >
            <span className="icon">
              {cat.icon_url ? (
                <img src={cat.icon_url} alt="" style={{ width: '3rem', height: '3rem', objectFit: 'contain', verticalAlign: 'middle' }} />
              ) : cat.icon}
            </span>
            <span>{cat.name}</span>
          </button>
        ))}
      </nav>

      <main className="main-content">
        {view === 'studio' && studioPrompt && (
          <Studio
            key={`studio-${studioReflection?.id || 'new'}-${studioKey}`}
            prompt={studioPrompt}
            token={token}
            API_URL={`${API_BASE_URL}/api`}
            onReflectionCreated={(id) => {
              updateURLParams({ studioReflectionId: id });
              // During tutorial with AI overrides, chain auto-enhance after auto-transcription
              if (tutorialIsActive && currentTutorial?.aiOverrides) {
                pendingEnhanceAfterTranscribeRef.current = id;
              }
            }}
            parentReflectionId={studioParentReflectionId}
            followupQuestion={studioFollowupQuestion}
            authorizeAI={effectiveAISettings.authorizeAi}
            automateTitles={effectiveAISettings.automateTitles}
            enableFollowups={effectiveAISettings.enableFollowups}
            autoEnhance={effectiveAISettings.autoEnhance}
            lastEnhancementUpdate={lastEnhancementUpdate}
            lastTitleUpdate={lastTitleUpdate}
            onEnhance={handleEnhance}
            onOpenFollowup={(parentId, question) => openFollowupStudio(parentId, question, studioPrompt, studioReturnContextRef.current?.sourceCardId)}
            onViewChildReflection={async (childReflectionId) => {
              if (!childReflectionId) return;
              try {
                const res = await api.get(`/reflections/${childReflectionId}`);
                openStudio(studioPrompt, res.data, false, {
                  sourceCardId: studioReturnContextRef.current?.sourceCardId,
                  fromTree: true
                });
              } catch (err) {
                console.error('Error opening child reflection:', err);
              }
            }}
            onClose={() => {
              const returnContext = studioReturnContextRef.current;
              studioReturnContextRef.current = null;

              // Clear Studio state (including follow-up context)
              setStudioPrompt(null);
              setStudioReflection(null);
              setStudioReadOnly(false);
              setStudioParentReflectionId(null);
              setStudioFollowupQuestion(null);
              changeView('prompts');

              // Restore scroll position, prompt expansion, and tree state
              if (returnContext) {
                restorePromptsView(returnContext);
              }
            }}
            onRecordingStateChange={handleRecordingStateChange}
            getAxiosConfig={getAxiosConfig}
            transcriptionPreference={effectiveAISettings.transcriptionPreference}
            lastTranscriptionUpdate={lastTranscriptionUpdate}
            existingReflection={studioReflection}
            readOnly={studioReadOnly}
            storageWarning={storageWarningMessage}
            registerBeforeLeave={(fn) => { studioBeforeLeaveRef.current = fn; }}
            onDirtyStateChange={(dirty) => { studioIsDirtyRef.current = dirty; }}
            onNavigateToSettings={async () => {
              if (studioBeforeLeaveRef.current) {
                const result = await studioBeforeLeaveRef.current();
                if (result === 'cancel') return;
              }
              const params = new URLSearchParams({ page: 'settings', tab: 'preferences' });
              window.history.pushState({}, '', `?${params.toString()}`);
              window.dispatchEvent(new PopStateEvent('popstate'));
            }}
            onMarkViewed={(reflectionId) => {
              // Only mark shared reflections (from other users) as viewed
              const reflection = studioReflection;
              if (!reflection || reflection.user_id === user?.id) return;
              setReflectionViewStatuses(prev => ({
                ...prev,
                [reflectionId]: { ...prev[reflectionId], studio_opened: true, studio_opened_at: new Date().toISOString() }
              }));
              api.post('/reflection-views/studio-opened', { reflectionId }).catch(() => {});
            }}
          />
        )}
        {view === 'home' && (
          <Home
            user={user}
            token={token}
            API_URL={`${API_BASE_URL}/api`}
            categories={categories}
            onNavigateToPrompts={(categorySlug) => {
              if (categorySlug) {
                setSelectedCategory(categorySlug);
              }
              changeView('prompts');
            }}
            onNavigateToReflections={() => changeView('reflections')}
            onRecordReflection={(prompt) => openStudio(prompt)}
            onViewReflection={(reflection) => {
              // Navigate to the reflection's prompt and expand it
              if (reflection.prompt_id) {
                setSelectedPromptId(reflection.prompt_id);
                setExpandedPromptId(reflection.prompt_id);
              }
              changeView('prompts');
            }}
            onNavigateToUserShared={(username, newOnly) => {
              // Navigate to prompts with user filter
              setSearchText(`user:${username}`);
              if (newOnly) {
                setAnswerFilter('new only');
              } else {
                setAnswerFilter('answered only');
              }
              setHideShared(false); // Make sure shared reflections are visible
              changeView('prompts');
            }}
            onNavigateToFriendSuggestion={(promptId) => {
              setSelectedCategory('friend-suggestions');
              setSelectedPromptId(promptId);
              setExpandedPromptId(promptId);
              changeView('prompts');
              setTimeout(() => {
                const el = document.querySelector(`[data-prompt-id="${promptId}"]`);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }, 400);
            }}
          />
        )}
        {view === 'prompts' ? (
          <>
            <div className="add-prompt-button-container">
              <div className="filter-container">
                <button
                  className="filter-toggle-button"
                  onClick={() => setShowFilterBox(!showFilterBox)}
                >
                  🔍 Filter {activeFilterCount}
                </button>
              </div>
              <button
                className="add-prompt-button"
                onClick={() => setShowAddPromptModal(true)}
              >
                ➕ Add New Prompt
              </button>
            </div>
            {showFilterBox && (
              <FilterBox
                hideShared={hideShared}
                onHideSharedChange={setHideShared}
                searchText={searchText}
                onSearchTextChange={setSearchText}
                answerFilter={answerFilter}
                onAnswerFilterChange={setAnswerFilter}
                dateFilterType={dateFilterType}
                onDateFilterTypeChange={setDateFilterType}
                dateFilterSince={dateFilterSince}
                onDateFilterSinceChange={setDateFilterSince}
                dateFilterFrom={dateFilterFrom}
                onDateFilterFromChange={setDateFilterFrom}
                dateFilterTo={dateFilterTo}
                onDateFilterToChange={setDateFilterTo}
                onReset={() => {
                  setHideShared(false);
                  setSearchText('');
                  setAnswerFilter('show all');
                  setDateFilterType('none');
                  setDateFilterSince('');
                  setDateFilterFrom('');
                  setDateFilterTo('');
                }}
              />
            )}
            <div className="prompts-list">
              {prompts.filter((prompt) => {
                // Filter 1: Hide Shared
                if (hideShared) {
                  const sharedCount = parseInt(prompt.shared_count) || 0;
                  if (sharedCount > 0) {
                    // Check if this prompt has any shared reflections
                    const reflections = promptReflections[prompt.id] || [];
                    const hasSharedReflections = reflections.some(r => r.user_id !== user?.id);
                    if (hasSharedReflections && reflections.every(r => r.user_id !== user?.id)) {
                      // All reflections are shared, hide this prompt
                      return false;
                    }
                  }
                }

                // Filter 2: Search Text - supports user:xxx, title:xxx, text:xxx patterns
                if (searchText.trim()) {
                  const reflections = promptReflections[prompt.id] || [];

                  // Prompt text matches title: and general text searches
                  const promptMatches = promptMatchesSearch(prompt.prompt_text, searchText);
                  // Any reflection matches the full search criteria
                  const hasReflectionMatch = reflections.some(r => matchesSearch(r, searchText));

                  if (!promptMatches && !hasReflectionMatch) return false;
                }

                // Filter 2.5: Date Filter
                // Hide prompts that have no reflections matching the date filter
                // BUT only if we've actually loaded the reflections for this prompt
                if (dateFilterType !== 'none') {
                  const reflections = promptReflections[prompt.id];
                  // Only apply filter if reflections are loaded
                  if (reflections && reflections.length > 0) {
                    const hasMatchingReflection = reflections.some(r => matchesDateFilter(r));
                    if (!hasMatchingReflection) return false;
                  }
                  // If reflections not loaded yet, show the prompt (don't filter it out)
                }

                // Filter 3: Answer Filter
                // When there's a user filter, use actual loaded reflections for accurate filtering
                const { userFilter } = parseSearchText(searchText);

                if (answerFilter === 'unanswered only') {
                  const myCount = parseInt(prompt.my_count) || 0;
                  if (myCount > 0) return false;
                } else if (answerFilter === 'answered only') {
                  if (userFilter) {
                    // With user filter, check if there are any reflections matching the user
                    const reflections = promptReflections[prompt.id] || [];
                    const matchingReflections = reflections.filter(r => matchesSearch(r, searchText));
                    if (matchingReflections.length === 0) return false;
                  } else {
                    // No user filter, use database counts
                    const myCount = parseInt(prompt.my_count) || 0;
                    const sharedCount = parseInt(prompt.shared_count) || 0;
                    // Show prompts that have any reflections visible (respecting hideShared filter)
                    const visibleSharedCount = hideShared ? 0 : sharedCount;
                    if (myCount === 0 && visibleSharedCount === 0) return false;
                  }
                } else if (answerFilter === 'new only') {
                  const reflections = promptReflections[prompt.id];
                  if (reflections === undefined) {
                    // Reflections not loaded yet, use backend-provided count
                    if ((parseInt(prompt.unviewed_count) || 0) === 0) return false;
                  } else {
                    // If there's a user filter, only check reflections from that user
                    const relevantReflections = userFilter
                      ? reflections.filter(r => matchesSearch(r, searchText))
                      : reflections;
                    const hasNewReflection = relevantReflections.some(r => isReflectionNew(r));
                    if (!hasNewReflection) return false;
                  }
                }

                return true;
              }).map((prompt, idx) => {
              const myCount = parseInt(prompt.my_count) || 0;
              const sharedCount = parseInt(prompt.shared_count) || 0;
              const isExpanded = expandedPromptId === prompt.id;
              const isSelected = selectedPromptId === prompt.id;
              const allReflections = promptReflections[prompt.id] || [];

              // Compute tree-inclusive match sets for search filtering
              const { matchIds, visibleIds } = getTreeMatchSets(allReflections, searchText);

              // Filter reflections based on current filter criteria
              const filteredReflections = allReflections.filter(r => {
                // Filter by followup display mode - hide child reflections when mode is 'only_as_threads'
                if (effectiveAISettings.followupDisplayMode === 'only_as_threads' && r.parent_reflection_id) {
                  return false;
                }

                // Filter by hideShared
                if (hideShared && r.user_id !== user?.id) {
                  return false;
                }

                // Filter by answerFilter (new only)
                if (answerFilter === 'new only' && !isReflectionNew(r)) {
                  return false;
                }

                // Filter by searchText - tree-inclusive: show all nodes in a tree if any node matches
                if (searchText.trim() && visibleIds.size > 0 && !visibleIds.has(r.id)) {
                  return false;
                }
                // When search is active but no reflections match, fall back to standard matchesSearch
                if (searchText.trim() && visibleIds.size === 0 && !matchesSearch(r, searchText)) {
                  return false;
                }

                // Filter by date
                if (!matchesDateFilter(r)) {
                  return false;
                }

                return true;
              });

              // Calculate total counts from all reflections (includes followups)
              const totalMyCount = allReflections.filter(r => r.user_id === user?.id).length;
              const totalSharedCount = allReflections.filter(r => r.user_id !== user?.id).length;

              // For display counts: use total counts if reflections are loaded,
              // otherwise use database counts but respect hideShared filter
              const displayMyCount = allReflections.length > 0 ? totalMyCount : myCount;
              const displaySharedCount = allReflections.length > 0 ? (hideShared ? 0 : totalSharedCount) : (hideShared ? 0 : sharedCount);

              // Show View button only if there are reflections to display after filtering
              const hasReflections = displayMyCount > 0 || displaySharedCount > 0;

              const shouldBlur = isRecording && recordingPromptId !== prompt.id;
              const promptIsNew = promptHasNewReflections(prompt);
              const promptIsMatch = !!(searchText.trim() && promptMatchesSearch(prompt.prompt_text, searchText));

              return (
                <div
                  key={idx}
                  className={`prompt-card ${isSelected ? 'expanded' : ''} ${shouldBlur ? 'blur-when-recording' : ''}`}
                  data-prompt-id={prompt.id}
                  data-has-reflections={hasReflections ? 'true' : undefined}
                  onClick={() => setSelectedPromptId(prompt.id)}
                  style={{ position: 'relative' }}
                >
                  {promptIsNew && (
                    <div
                      className="new-badge prompt-new-badge"
                      onClick={(e) => handleDismissPromptBadge(prompt, e)}
                    >
                      NEW
                    </div>
                  )}
                  {promptIsMatch && (
                    <div className={`match-badge prompt-match-badge ${promptIsNew ? 'with-new' : ''}`}>
                      Match
                    </div>
                  )}
                  <div className="prompt-main">
                    <div className="prompt-content">
                      <p className="prompt-text">{prompt.prompt_text}</p>
                      <p className="prompt-category">{prompt.category_name}</p>
                      {prompt.created_by_username && (
                        <div className="friend-suggestion-meta">
                          <span className="friend-suggestion-from">💌 Suggested by {prompt.created_by_username}</span>
                          <span className="friend-suggestion-date">{formatDateOnly(prompt.created_at, user?.timezone)}</span>
                          {prompt.personal_note && (
                            <p className="friend-suggestion-note">"{prompt.personal_note}"</p>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="prompt-actions">
                      {hasReflections && (
                        <button
                          className="reflection-btn-show"
                          onClick={() => togglePromptExpansion(prompt)}
                        >
                          <span className="icon">👁️</span>
                          <div className="view-counts">
                            <span>View ({displayMyCount})</span>
                            {displaySharedCount > 0 && <span>Others ({displaySharedCount})</span>}
                          </div>
                        </button>
                      )}
                      <button
                        className="reflection-btn-edit"
                        onClick={() => openStudio(prompt)}
                      >
                        <span className="icon">➕</span>
                        Add Reflection
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="prompt-reflections">
                      {filteredReflections.map((reflection) => (
                        <ReflectionCard
                          key={reflection.id}
                          reflection={reflection}
                          isShared={reflection.user_id !== user?.id}
                          isNew={isReflectionTreeNew(reflection, allReflections)}
                          isMatch={!!(searchText.trim() && matchIds.has(reflection.id))}
                          searchText={searchText}
                          isRecording={isRecording}
                          isAdmin={isAdmin()}
                          promptId={prompt.id}
                          onDelete={handleDeleteReflectionClick}
                          onDismissBadge={handleDismissBadge}
                          onRecordingStateChange={handleRecordingStateChange}
                          onViewStatusChange={handleViewStatusChange}
                          transcriptionPreference={effectiveAISettings.transcriptionPreference}
                          currentUserId={user?.id}
                          onTranscriptionUpdate={handleTranscriptionUpdate}
                          onChangePrompt={handleChangePromptClick}
                          onOpenStudio={(r, readOnly, opts) => openStudio(prompt, r, !!readOnly, opts)}
                          showMediaSize={userPreferences.show_reflection_sizes !== false}
                          onEnhance={handleEnhance}
                          authorizeAI={effectiveAISettings.authorizeAi}
                          enableFollowups={effectiveAISettings.enableFollowups}
                          onOpenFollowup={(parentId, question) => openFollowupStudio(parentId, question, prompt, reflection.id)}
                          onTranscribeAndEnhance={handleTranscribeAndEnhance}
                          lastEnhancementUpdate={lastEnhancementUpdate}
                          initialTreeExpanded={treeAutoExpandCardId === reflection.id}
                          timezone={user?.timezone}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            </div>
          </>
        ) : view === 'reflections' ? (
          <div className="reflections-list">
            {(() => {
              // Compute tree-inclusive match sets once for all reflections
              const { matchIds: reflMatchIds, visibleIds: reflVisibleIds } = getTreeMatchSets(reflections, searchText);

              return reflections.filter((reflection) => {
              // Filter by followup display mode - hide child reflections when mode is 'only_as_threads'
              if (effectiveAISettings.followupDisplayMode === 'only_as_threads' && reflection.parent_reflection_id) {
                return false;
              }

              // Apply hideShared filter
              if (hideShared && reflection.user_id !== user?.id) {
                return false;
              }

              // Apply search text filter - tree-inclusive
              if (searchText.trim() && reflVisibleIds.size > 0 && !reflVisibleIds.has(reflection.id)) {
                return false;
              }
              if (searchText.trim() && reflVisibleIds.size === 0 && !matchesSearch(reflection, searchText)) {
                return false;
              }

              // Apply date filter
              if (!matchesDateFilter(reflection)) {
                return false;
              }

              // Apply answer filter
              if (answerFilter === 'unanswered only') {
                // Show only prompts that user hasn't answered
                if (reflection.user_id === user?.id) {
                  return false;
                }
              } else if (answerFilter === 'answered only') {
                // Show only reflections from prompts user has answered
                // This is tricky in reflections view - we'll need to check if user has any reflection for this prompt
                // For now, we'll just show all in this view when "answered only" is selected
                // since we don't have easy access to all reflections per prompt
              } else if (answerFilter === 'new only') {
                // Show only new reflections
                if (!isReflectionNew(reflection)) {
                  return false;
                }
              }

              return true;
            }).map((reflection) => (
              <ReflectionCard
                key={reflection.id}
                reflection={reflection}
                isShared={reflection.created_by !== user?.username}
                isNew={isReflectionTreeNew(reflection, reflections)}
                isMatch={!!(searchText.trim() && reflMatchIds.has(reflection.id))}
                searchText={searchText}
                isRecording={isRecording}
                isAdmin={isAdmin()}
                showPromptText
                promptId={reflection.prompt_id}
                onDelete={handleDeleteReflectionClick}
                onDismissBadge={handleDismissBadge}
                onRecordingStateChange={handleRecordingStateChange}
                onViewStatusChange={handleViewStatusChange}
                transcriptionPreference={effectiveAISettings.transcriptionPreference}
                currentUserId={user?.id}
                onTranscriptionUpdate={handleTranscriptionUpdate}
                onChangePrompt={handleChangePromptClick}
                onOpenStudio={(r, readOnly, opts) => openStudio({ id: r.prompt_id, prompt_text: r.prompt_text, category_slug: r.category_slug }, r, !!readOnly, opts)}
                showMediaSize={userPreferences.show_reflection_sizes !== false}
                onEnhance={handleEnhance}
                authorizeAI={effectiveAISettings.authorizeAi}
                enableFollowups={effectiveAISettings.enableFollowups}
                onOpenFollowup={(parentId, question) => openFollowupStudio(parentId, question, { id: reflection.prompt_id, prompt_text: reflection.prompt_text, category_slug: reflection.category_slug }, reflection.id)}
                onTranscribeAndEnhance={handleTranscribeAndEnhance}
                lastEnhancementUpdate={lastEnhancementUpdate}
                initialTreeExpanded={treeAutoExpandCardId === reflection.id}
                timezone={user?.timezone}
              />
            ));
            })()}
          </div>
        ) : view === VIEWS.FAMILY_ARCHIVE ? (
          <FamilyArchiveView user={user} />
        ) : null}
      </main>

      {showPasswordModal && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="modal-content password-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Change Password</h2>
            <div className="profile-form">
              <div className="form-group">
                <label>Current Password</label>
                <PasswordInput
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>New Password</label>
                <PasswordInput
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Confirm New Password</label>
                <PasswordInput
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                />
              </div>
              <div className="modal-actions">
                <button onClick={handlePasswordChange}>Change Password</button>
                <button onClick={() => setShowPasswordModal(false)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showForgotPassword && (
        <div className="modal-overlay" onClick={() => { setShowForgotPassword(false); setResetMessage(''); }}>
          <div className="modal-content password-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Forgot Password</h2>
            <p>Enter your email address and we'll send you a link to reset your password.</p>
            
            <form onSubmit={handleForgotPassword} className="profile-form">
              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  placeholder="Enter your email"
                />
              </div>
              
              {resetMessage && (
                <div className="reset-message">{resetMessage}</div>
              )}
              
              <div className="modal-actions">
                <button type="submit">Send Reset Link</button>
                <button type="button" onClick={() => { setShowForgotPassword(false); setResetMessage(''); }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showResetPassword && (
        <div className="modal-overlay">
          <div className="modal-content password-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Reset Password</h2>
            
            {resetSuccess ? (
              <div className="success-message">
                <p>✓ Password reset successfully!</p>
                <p>Redirecting to login...</p>
              </div>
            ) : (
              <form onSubmit={handleResetPassword} className="profile-form">
                <div className="form-group">
                  <label>New Password</label>
                  <PasswordInput
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    placeholder="Enter new password"
                  />
                </div>

                <div className="form-group">
                  <label>Confirm New Password</label>
                  <PasswordInput
                    name="confirmNewPassword"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    required
                    placeholder="Confirm new password"
                  />
                </div>
                
                {resetError && (
                  <div className="error-message">{resetError}</div>
                )}
                
                <div className="modal-actions">
                  <button type="submit">Reset Password</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Delete Reflection Confirmation Modal */}
      {showDeleteReflectionModal && reflectionToDelete && (
        <DeleteConfirmationModal
          reflection={reflectionToDelete}
          onConfirm={confirmDeleteReflection}
          onCancel={cancelDeleteReflection}
        />
      )}

      {/* Change Prompt Modal */}
      {showChangePromptModal && changePromptReflection && (
        <ChangePromptModal
          reflection={changePromptReflection}
          prompts={allActivePrompts}
          aiEnabled={userPreferences.authorize_ai === 'true'}
          onClose={cancelChangePrompt}
          onAccept={handleChangePromptAccept}
          onPromptCreated={handlePromptCreated}
        />
      )}

      {/* Add New Prompt Modal */}
      {showAddPromptModal && (
        <AddPromptModal
          categories={categories}
          onSave={handleSaveNewPrompt}
          onClose={() => setShowAddPromptModal(false)}
        />
      )}

      {/* Storage / Plan Limit Dialog */}
      <StorageLimitDialog
        show={showStorageLimitDialog}
        storageStatus={storageStatus}
        limitType={storageLimitType}
        onClose={() => setShowStorageLimitDialog(false)}
        onUpgrade={() => {
          setShowStorageLimitDialog(false);
          // Navigate to Plan & Billing settings tab
          const params = new URLSearchParams({ page: 'settings', tab: 'subscription' });
          window.history.pushState({}, '', `?${params.toString()}`);
          window.dispatchEvent(new PopStateEvent('popstate'));
        }}
      />

      {/* Processing Dialog for AI operations */}
      <ProcessingDialog
        show={!!processingDialog}
        text={processingDialog?.text || ''}
      />

      {/* Tutorial Overlay */}
      <TutorialOverlay />

      {/* One-time chance to verify phone, shown when email was verified first and phone is still pending */}
      {showPhoneVerificationNudge ? (
        <PhoneVerificationNudge
          phone={verificationData.phone}
          verificationCode={verificationCode}
          onVerificationCodeChange={setVerificationCode}
          onVerifyCode={handleVerifyCode}
          onResendVerification={handleResendVerification}
          resendCooldown={resendCooldown}
          resending={resending}
          onSkip={dismissPhoneVerificationNudge}
        />
      ) : (
        <>
          {/* Privacy Choices Dialog - first-time accounts only, shown before WelcomeDialog */}
          {showWelcomeDialog && !isWelcomeBack && !privacyChoicesShown && (
            <PrivacyChoicesDialog
              user={user}
              onContinue={() => setPrivacyChoicesShown(true)}
            />
          )}

          {/* Welcome Dialog for first-time or returning users */}
          {showWelcomeDialog && (isWelcomeBack || privacyChoicesShown) && (
            <WelcomeDialog
              isWelcomeBack={isWelcomeBack}
              onSkip={() => setShowWelcomeDialog(false)}
              onStartTutorial={() => {
                setShowWelcomeDialog(false);
                changeView('prompts');
                startTutorial(studioTutorial);
              }}
            />
          )}
        </>
      )}

      {showExportModal && (
        <ExportModal
          token={token}
          apiUrl={`${API_BASE_URL}/api`}
          onClose={() => setShowExportModal(false)}
        />
      )}
    </div>
  );
}

export default App;
